import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  UserIcon, 
  MailIcon, 
  CalendarIcon, 
  ShieldIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  PhoneIcon,
  MapPinIcon
} from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { getCountryTranslation } from '../../../services/countriesTranslationsService';
import { getRegionTranslation } from '../../../services/regionsTranslationsService';
import { getAcademyTranslation } from '../../../services/academiesTranslationsService';
import { getDistrictTranslation } from '../../../services/districtsTranslationsService';
import { getInstitutionTranslation } from '../../../services/institutionsTranslationsService';
import { getDepartmentTranslation } from '../../../services/departmentsTranslationsService';

const UserProfile = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('UserProfile mounted with userId:', userId);
    console.log('Current URL:', window.location.href);
    console.log('Current pathname:', window.location.pathname);
    console.log('Expected path:', `/dashboard/users/${userId}`);
    fetchUserProfile();
  }, [userId]);

  const fetchUserProfile = async () => {
    setLoading(true);
    console.log('Fetching user profile for userId:', userId);
    try {
      const response = await api.get(`/users/${userId}`);
      console.log('User profile response:', response.data);
      if (response.data.success) {
        // البيانات الحقيقية من API
        const userData = response.data.data;
        console.log('User data from API:', userData);
        setUser(userData);
        console.log('User data set successfully');
      } else {
        console.error('API returned success: false');
        toast.error(t('profile.loadUserFailed'), { duration: 3000, position: 'top-center' });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      console.error('Error details:', error.response?.data);
      toast.error(t('profile.loadUserError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };


  const getRoleIcon = (roleName) => {
    switch (roleName) {
      case 'super_admin':
        return <ShieldIcon className="w-5 h-5 text-red-600" />;
      case 'admin':
        return <ShieldIcon className="w-5 h-5 text-purple-600" />;
      case 'manager':
        return <ShieldIcon className="w-5 h-5 text-blue-600" />;
      case 'supervisor':
        return <ShieldIcon className="w-5 h-5 text-green-600" />;
      case 'trainer':
        return <ShieldIcon className="w-5 h-5 text-orange-600" />;
      default:
        return <UserIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('profile.notAvailable');
    const date = new Date(dateString);
    return date.toLocaleDateString(
      currentLanguage === 'en' ? 'en-US' : 'fr-FR',
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    );
  };

  const formatDateOfBirth = (dateString) => {
    if (!dateString) return t('profile.notAvailable');
    const date = new Date(dateString);
    return date.toLocaleDateString(
      currentLanguage === 'en' ? 'en-US' : 'fr-FR',
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
    );
  };

  const getGenderText = (gender) => {
    switch (gender) {
      case 'male':
        return t('users.male');
      case 'female':
        return t('users.female');
      case 'other':
        return t('users.other');
      default:
        return t('profile.notAvailable');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('profile.loadingUserProfile')}</h2>
          <p className="text-gray-600">{t('profile.pleaseWaitUserProfile')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('profile.userNotFound')}</h2>
          <p className="text-gray-600">{t('profile.userNotFoundDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeftIcon className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          {t('common.back')}
        </button>
      </div>

      {/* Profile Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg p-6 text-white">
        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">
              {(user?.user?.first_name || user?.first_name)?.charAt(0)}{(user?.user?.last_name || user?.last_name)?.charAt(0)}
            </span>
          </div>
          <div>
            <h2 className="text-2xl font-bold">
              {user?.user?.first_name || user?.first_name} {user?.user?.last_name || user?.last_name}
            </h2>
            <p className="text-primary-100">
              {(user?.roles?.[0] || user?.user?.roles?.[0]) ? t(`roles.${(user?.roles?.[0] || user?.user?.roles?.[0]).name.toUpperCase()}`) : t('profile.notAvailable')}
            </p>
            <p className="text-sm text-primary-200">
              {user?.user?.email || user?.email}
            </p>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white shadow-lg rounded-lg border border-gray-200">
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Personal Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserIcon className={`w-5 h-5 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('profile.personalInfo')}
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.firstName')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {user?.user?.first_name || user?.first_name || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.lastName')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {user?.user?.last_name || user?.last_name || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('common.email')}
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <span className="text-gray-900 font-medium">
                        {user?.user?.email || user?.email || t('profile.notAvailable')}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('users.phone')}
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center">
                        <PhoneIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <span className="text-gray-900 font-medium">
                          {user?.user?.phone || user?.phone || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.dateOfBirth')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          <CalendarIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span className="text-gray-900 font-medium">
                            {(user?.user?.date_of_birth || user?.date_of_birth) ? formatDateOfBirth(user?.user?.date_of_birth || user?.date_of_birth) : t('profile.notAvailable')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.gender')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {getGenderText(user?.user?.gender || user?.gender)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Organizational Access */}
                {(user?.organizationalAccess || user?.user?.organizationalAccess) && (user?.organizationalAccess?.length > 0 || user?.user?.organizationalAccess?.length > 0) && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <MapPinIcon className={`w-5 h-5 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t('profile.organizationalAccess')}
                    </h3>
                    <div className="space-y-3">
                      {(user?.organizationalAccess || user?.user?.organizationalAccess || []).map((org, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">{t('profile.country')}:</span>
                              <span className="text-sm text-gray-900">{getCountryTranslation(org.country_name, currentLanguage)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">{t('profile.region')}:</span>
                              <span className="text-sm text-gray-900">{getRegionTranslation(org.region_name, currentLanguage)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">{t('profile.academy')}:</span>
                              <span className="text-sm text-gray-900">{getAcademyTranslation(org.academy_name, currentLanguage)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">{t('profile.district')}:</span>
                              <span className="text-sm text-gray-900">{getDistrictTranslation(org.district_name, currentLanguage)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">{t('profile.institution')}:</span>
                              <span className="text-sm text-gray-900">{getInstitutionTranslation(org.institution_name, currentLanguage)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">{t('profile.department')}:</span>
                              <span className="text-sm text-gray-900">{getDepartmentTranslation(org.department_name, currentLanguage)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Account Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ShieldIcon className={`w-5 h-5 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('profile.accountInfo')}
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.username')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          {user?.user?.username || user?.username || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>

                    {/* User ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.userId')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <span className="text-gray-900 font-medium">
                          #{user?.user?.id || user?.id || t('profile.notAvailable')}
                        </span>
                      </div>
                    </div>

                    {/* Account Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.accountStatus')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          {(user?.user?.is_active || user?.is_active) ? (
                            <CheckCircleIcon className={`w-4 h-4 text-green-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          ) : (
                            <XCircleIcon className={`w-4 h-4 text-red-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          )}
                          <span className={`text-sm font-medium ${(user?.user?.is_active || user?.is_active) ? 'text-green-600' : 'text-red-600'}`}>
                            {(user?.user?.is_active || user?.is_active) ? t('profile.active') : t('profile.inactive')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Verification Status */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.verificationStatus')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          {(user?.user?.is_verified || user?.is_verified) ? (
                            <CheckCircleIcon className={`w-4 h-4 text-green-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          ) : (
                            <XCircleIcon className={`w-4 h-4 text-red-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          )}
                          <span className={`text-sm font-medium ${(user?.user?.is_verified || user?.is_verified) ? 'text-green-600' : 'text-red-600'}`}>
                            {(user?.user?.is_verified || user?.is_verified) ? t('profile.verified') : t('profile.unverified')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Last Login */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.lastLogin')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          <CalendarIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span className="text-gray-900 font-medium">
                            {(user?.user?.last_login || user?.last_login) ? formatDate(user?.user?.last_login || user?.last_login) : t('profile.notAvailable')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Created At */}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('profile.createdAt')}
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center">
                          <CalendarIcon className={`w-4 h-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span className="text-gray-900 font-medium">
                            {(user?.user?.created_at || user?.created_at) ? formatDate(user?.user?.created_at || user?.created_at) : t('profile.notAvailable')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Roles Information */}
                {(user?.roles || user?.user?.roles) && (user?.roles?.length > 0 || user?.user?.roles?.length > 0) && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <ShieldIcon className={`w-5 h-5 text-primary-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t('profile.roles')}
                    </h3>
                    <div className="space-y-3">
                      {(user?.roles || user?.user?.roles || []).map((role, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              {getRoleIcon(role.name)}
                              <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                                <h5 className="text-sm font-medium text-gray-900">
                                  {t(`roles.${role.name.toUpperCase()}`)}
                                </h5>
                                <p className="text-sm text-gray-500 mt-1">
                                  {role.description}
                                </p>
                              </div>
                            </div>
                            <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                                {t('profile.level')} {role.level}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
